import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';

import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  DOCUMENT,
  inject,
} from '@angular/core';
import { Router, RouterOutlet, ActivatedRoute } from '@angular/router';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { SessionService } from '@services/session.service';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { isStorePreview } from './features/wheel-store/store-preview';
import { isEngineWheel } from './features/engine/engine-route';
import { EngineService } from './features/engine/engine.service';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [AsyncPipe, RouterOutlet],
})
export class AppComponent implements OnInit {
  private document = inject<Document>(DOCUMENT);
  private route = inject(ActivatedRoute);
  private breakpointObserver = inject(BreakpointObserver);
  private session = inject(SessionService);
  private router = inject(Router);
  readonly engine = inject(EngineService);

  reload(): void {
    window.location.reload();
  }

  isMobile$ = this.session.isMobile$;
  game$ = this.session.game$;
  theme$ = this.session.theme$;

  ngOnInit(): void {
    this.updateBodyClass();
    this.checkRouting();
  }

  private checkRouting(): void {
    if (isStorePreview() || isEngineWheel()) {
      this.breakpointObserver
        .observe([Breakpoints.Handset])
        .pipe(untilDestroyed(this))
        .subscribe((result) => this.session.setIsMobile(result.matches));
      return;
    }
    combineLatest([
      this.breakpointObserver.observe([Breakpoints.Handset]),
      this.route.queryParams,
    ])
      .pipe(
        untilDestroyed(this),
        filter(([, queryParams]) => !!Object.keys(queryParams).length),
      )
      .subscribe(([result, queryParams]) => {
        const urlToNavigate = window.location.pathname;
        if (result.matches) {
          this.session.setIsMobile(true);
          if (!urlToNavigate.includes('/m')) {
            const url = urlToNavigate.includes('/d')
              ? urlToNavigate.replace('/d', '/m')
              : '/m';
            this.router.navigate([url], {
              queryParams,
            });
          }
        } else if (!result.matches) {
          this.session.setIsMobile(false);
          if (!urlToNavigate.includes('/d')) {
            const url = urlToNavigate.includes('/m')
              ? urlToNavigate.replace('/m', '/d')
              : '/d';
            this.router.navigate([url], {
              queryParams,
            });
          }
        }
      });
  }

  private updateBodyClass(): void {
    const bodyClass = 'mobile-version';
    this.isMobile$.pipe(untilDestroyed(this)).subscribe((isMobile) => {
      if (isMobile) {
        this.document.body.classList.add(bodyClass);
      } else {
        this.document.body.classList.remove(bodyClass);
      }
    });
    this.theme$
      .pipe(
        filter((theme) => !!theme),
        take(1),
      )
      .subscribe((theme) => {
        if (theme!.desktopFullResolutionFormat) {
          this.document.body.classList.add('full-format');
        }
      });
  }
}
